import type { Dispatch, SetStateAction } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';

interface OverrideIdContextType {
  accountId: string | undefined;
  setAccountId: Dispatch<SetStateAction<string | undefined>>;
  projectId: string | undefined;
  setProjectId: Dispatch<SetStateAction<string | undefined>>;
  reset: Dispatch<SetStateAction<void>>;
}

export const OverrideIdContext = createContext<OverrideIdContextType>(
  new Proxy({} as any, {
    get: () => {
      throw new Error('You are not within a OverrideIdContext');
    },
  }),
);

export const OverrideIdProvider: ComponentWithChildren = ({ children }) => {
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const reset = useCallback(() => {
    setAccountId(undefined);
    setProjectId(undefined);
  }, []);

  const value = useMemo(
    () => ({
      accountId,
      setAccountId,
      projectId,
      setProjectId,
      reset,
    }),
    [accountId, projectId, reset],
  );

  return (
    <OverrideIdContext.Provider value={value}>
      {children}
    </OverrideIdContext.Provider>
  );
};

export const useOverrideIdContext = () => useContext(OverrideIdContext);
